import { makeAutoObservable, runInAction } from "mobx";

import { IS_DEV } from "@/utils/env";
import {
  buildCollectionTailKey,
  buildThreadKeyFromDeal,
  dealMatchesThreadKey,
  normalizeDealLensRouteSegment,
  normalizeDealLensThreadEmail,
  normalizeDeallensIdFromUrl,
  resolveThreadTailKey,
} from "./dealLensThreadCacheKey";
import { cachedPropertyIdMatchesKey } from "@/store/propertyCore/propertyCacheUtils";
import { ACTIVATION_STATE } from "@/store/propertyCore/propertyApi";
import { isDealLensRowAvailable } from "./dealAvailability";
import { carryForwardDealEnrichment, isDealPropertyHydrated } from "./dealPropertyUtils";
import { buildHubDisplayRow, mergeDealPatch, substituteDealRow } from "./dealOfferThreadUtils";
import {
  fetchDealLensByThread,
  fetchDealLensThreadTip,
} from "./dealsApi";
import { mergeDealLensSwipeStatus } from "./dealsConstants";

/** Shown when list viewer email is not a participant on the thread. */
export const DEAL_LENS_VIEWER_MISMATCH_MESSAGE =
  "Deal thread does not match the current viewer.";

const RECONCILE_DELAYS_MS = [0, 500, 1000, 2000];

function devLog(...args) {
  if (IS_DEV) console.log(...args);
}

function rowDealId(d) {
  return String(d?.id ?? d?.deallens_id ?? "").trim();
}

function normalizedRowDealId(d) {
  return rowDealId(d).replace(/^deallens#/i, "");
}

function createOptimisticId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return `optimistic:${crypto.randomUUID()}`;
  }
  return `optimistic:${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function wait(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Thread-tail registry: per-conversation tip cache, optimistic appends,
 * reconciliation, prefetch, and detail resolution.
 *
 * The `host` back-reference provides list-level state managed by DeallensStore.
 *
 * @param {{ listCustomer: string|null, listUserEmail: string|null, listDeals: object[], _resetGeneration: number, _syncOpportunitiesListCache: Function, _invalidateActiveOpportunitiesListCache: Function }} host
 */
class DealLensThreadTailCache {
  /** @type {Map<string, { tail: object, pending: boolean, previousTail: object|null, fetchedAt: number, detailFetchedAt: number|null }>} */
  threadTails = new Map();
  _aliases = new Map();
  _inFlightDetail = new Map();
  _inFlightReconcile = new Map();

  constructor(host) {
    /** @type {typeof host} */
    this._host = host;
    makeAutoObservable(this, {
      _host: false,
      _aliases: false,
      _inFlightDetail: false,
      _inFlightReconcile: false,
    }, { autoBind: true });
  }

  clear() {
    this.threadTails.clear();
    this._aliases.clear();
    this._inFlightDetail.clear();
    this._inFlightReconcile.clear();
  }

  // ── key helpers ──────────────────────────────────────────────────────

  _dealDateMs(deal) {
    const raw = deal?.last_modified || deal?.insert_date || null;
    if (!raw) return 0;
    const ts = new Date(raw).getTime();
    return Number.isFinite(ts) ? ts : 0;
  }

  _entry(threadKey) {
    if (!threadKey) return null;
    return this.threadTails.get(threadKey) ?? null;
  }

  _resolveKey(deal, rawUrlParam, routeSenderId, routeReceiverId) {
    const key = resolveThreadTailKey(deal, rawUrlParam, routeSenderId, routeReceiverId);
    return this._aliases.get(key) ?? key;
  }

  /**
   * @param {object} deal
   * @param {{ collectionScoped?: boolean }} [options]
   * @returns {string}
   */
  threadKeyForDeal(deal, { collectionScoped = false, collectionId = null } = {}) {
    const threadKey = buildThreadKeyFromDeal(deal);
    const coll = collectionId ?? deal?.collection_id;
    return collectionScoped ? buildCollectionTailKey(threadKey, coll) : threadKey;
  }

  _sortNewestFirst(deals = []) {
    if (!Array.isArray(deals) || deals.length <= 1) return deals;
    return [...deals].sort((a, b) => this._dealDateMs(b) - this._dealDateMs(a));
  }

  _serverTailAdvances(entry, serverDeal) {
    if (!entry?.pending || !serverDeal) return false;
    const previousId = normalizedRowDealId(entry.previousTail);
    const serverId = normalizedRowDealId(serverDeal);
    return Boolean(previousId && serverId && !serverId.startsWith("optimistic:") && serverId !== previousId);
  }

  _detailAvailabilityMismatch(cachedDeal, listDeal) {
    if (!cachedDeal || !listDeal) return false;
    return !isDealLensRowAvailable(listDeal) && isDealLensRowAvailable(cachedDeal);
  }

  _detailCacheStaleForBootstrap(cachedDeal, listDeal) {
    if (!cachedDeal || !listDeal || typeof listDeal !== "object") return false;

    if (this._detailAvailabilityMismatch(cachedDeal, listDeal)) return true;

    const cachedId = normalizedRowDealId(cachedDeal);
    const listId = normalizedRowDealId(listDeal);
    if (
      listId &&
      cachedId &&
      listId !== cachedId &&
      !cachedId.startsWith("optimistic:")
    ) {
      return true;
    }

    const cachedMs = this._dealDateMs(cachedDeal);
    const listMs = this._dealDateMs(listDeal);
    return cachedMs > 0 && listMs > cachedMs;
  }

  /**
   * Clears stale detail cache for threads tied to a deactivated property.
   *
   * @param {string} propertyId
   * @returns {number}
   */
  invalidateDetailCacheForProperty(propertyId) {
    const normalizedPropertyId = String(propertyId ?? "").trim();
    if (!normalizedPropertyId) return 0;

    let updated = 0;
    runInAction(() => {
      for (const [threadKey, entry] of this.threadTails.entries()) {
        const tail = entry?.tail;
        if (!tail) continue;
        const dealPropertyId = tail.property_id ?? tail.property ?? "";
        if (
          !cachedPropertyIdMatchesKey(dealPropertyId, normalizedPropertyId) &&
          !cachedPropertyIdMatchesKey(normalizedPropertyId, dealPropertyId)
        ) {
          continue;
        }

        const nextPropertyDetails = tail.propertyDetails
          ? {
              ...tail.propertyDetails,
              active: false,
              activation_state: ACTIVATION_STATE.DEACTIVATED,
            }
          : tail.propertyDetails;
        const patchedTail = mergeDealPatch(tail, {
          active: false,
          ...(nextPropertyDetails ? { propertyDetails: nextPropertyDetails } : {}),
        });

        this.threadTails.set(threadKey, {
          ...entry,
          tail: patchedTail,
          detailFetchedAt: null,
        });
        updated += 1;
      }
    });
    return updated;
  }

  // ── public reads ─────────────────────────────────────────────────────

  getThreadTail(deal, { collectionScoped = false } = {}) {
    const threadKey = this.threadKeyForDeal(deal, { collectionScoped });
    if (!threadKey) return null;
    return this._entry(threadKey)?.tail ?? null;
  }

  getThreadTailForRoute(
    deal, rawUrlParam, routeSenderId, routeReceiverId,
    { collectionScoped = false, collectionId = null } = {}
  ) {
    const entry = this.getThreadTailEntryForRoute(
      deal, rawUrlParam, routeSenderId, routeReceiverId,
      { collectionScoped, collectionId },
    );
    return entry?.tail ?? null;
  }

  /**
   * @returns {{ tail: object, pending?: boolean, detailFetchedAt?: number|null } | null}
   */
  getThreadTailEntryForRoute(
    deal, rawUrlParam, routeSenderId, routeReceiverId,
    { collectionScoped = false, collectionId = null } = {}
  ) {
    const baseKey = this._resolveKey(deal, rawUrlParam, routeSenderId, routeReceiverId);
    const threadKey = collectionScoped
      ? buildCollectionTailKey(baseKey, collectionId ?? deal?.collection_id)
      : baseKey;
    if (!threadKey) return null;
    return this._entry(threadKey) ?? null;
  }

  // ── customer / viewer guards ─────────────────────────────────────────

  _shouldRejectForCustomer(deal) {
    const active = String(this._host.listCustomer ?? "").trim();
    if (!active) return false;
    const dealCust = String(deal?.customer ?? "").trim();
    if (!dealCust) return false;
    return dealCust !== active;
  }

  _resolveViewerEmail(viewerEmail) {
    if (viewerEmail !== undefined) return normalizeDealLensThreadEmail(viewerEmail);
    return normalizeDealLensThreadEmail(this._host.listUserEmail);
  }

  _routeMatchesViewer(routeSenderId, routeReceiverId, viewerEmail) {
    const viewer = this._resolveViewerEmail(viewerEmail);
    if (!viewer) return true;
    const sender = normalizeDealLensThreadEmail(normalizeDealLensRouteSegment(routeSenderId));
    const receiver = normalizeDealLensThreadEmail(normalizeDealLensRouteSegment(routeReceiverId));
    if (!sender.includes("@") || !receiver.includes("@")) return true;
    return sender === viewer || receiver === viewer;
  }

  _shouldBlockThreadForViewer({ deal, routeSenderId, routeReceiverId, viewerEmail }) {
    if (deal && typeof deal === "object") return false;
    return !this._routeMatchesViewer(routeSenderId, routeReceiverId, viewerEmail);
  }

  _shouldBlockCachedTailForViewer(cachedDeal, viewerEmail) {
    const viewer = this._resolveViewerEmail(viewerEmail);
    if (!viewer) return false;
    const sender = normalizeDealLensThreadEmail(cachedDeal?.sender);
    const receiver = normalizeDealLensThreadEmail(cachedDeal?.receiver);
    if (!sender.includes("@") || !receiver.includes("@")) return false;
    return sender !== viewer && receiver !== viewer;
  }

  _viewerMismatchResult() {
    return { success: false, message: DEAL_LENS_VIEWER_MISMATCH_MESSAGE, isForbidden: true };
  }

  // ── register / advance / revert ──────────────────────────────────────

  registerTail(
    deal,
    {
      detail = false,
      force = false,
      clearPending = false,
      threadKey: explicitKey,
      collectionScoped = false,
    } = {}
  ) {
    const threadKey = explicitKey || this.threadKeyForDeal(deal, { collectionScoped });
    if (!threadKey || !deal || typeof deal !== "object") return false;

    if (!force && this._shouldRejectForCustomer(deal)) return false;

    const existing = this._entry(threadKey);
    if (existing?.pending && !force && !clearPending) return false;

    const inMs = this._dealDateMs(deal);
    const exMs = existing?.tail ? this._dealDateMs(existing.tail) : 0;
    if (!force && !clearPending && existing?.tail) {
      if (inMs && exMs && inMs < exMs) return false;
      const sameId = normalizedRowDealId(deal) === normalizedRowDealId(existing.tail);
      if (inMs === exMs && sameId && !detail) {
        const enrichmentUpgrade =
          !isDealPropertyHydrated(existing.tail) && isDealPropertyHydrated(deal);
        if (!enrichmentUpgrade) return false;
      }
    }

    const stamped = (!deal.customer && this._host.listCustomer)
      ? { ...deal, customer: this._host.listCustomer }
      : deal;

    const sameTailId =
      existing?.tail &&
      normalizedRowDealId(stamped) === normalizedRowDealId(existing.tail);

    const now = Date.now();
    this.threadTails.set(threadKey, {
      tail: stamped,
      pending: clearPending ? false : (existing?.pending ?? false),
      previousTail: clearPending ? null : (existing?.previousTail ?? null),
      fetchedAt: now,
      detailFetchedAt: detail ? now : (sameTailId ? (existing?.detailFetchedAt ?? null) : null),
    });
    return true;
  }

  registerTailsFromList(deals = []) {
    if (!Array.isArray(deals)) return;
    for (const deal of deals) this.registerTail(deal);
  }

  pruneOrphanTails() {
    const listKeys = new Set();
    for (const deal of this._host.listDeals) {
      const key = this.threadKeyForDeal(deal);
      if (key) listKeys.add(key);
    }
    for (const [threadKey, entry] of this.threadTails.entries()) {
      if (entry?.pending) continue;
      if (!listKeys.has(threadKey)) this.threadTails.delete(threadKey);
    }
  }

  // ── list-row helpers (mutate host.listDeals) ─────────────────────────

  _replaceListRow(threadKey, tailDeal) {
    if (!threadKey || !tailDeal) return;
    let found = false;
    this._host.listDeals = this._host.listDeals.map((d) => {
      if (dealMatchesThreadKey(d, threadKey)) { found = true; return tailDeal; }
      return d;
    });
    this._host.listDeals = found
      ? this._sortNewestFirst(this._host.listDeals)
      : this._sortNewestFirst([tailDeal, ...this._host.listDeals]);
    this._host._syncOpportunitiesListCache();
  }

  /**
   * Mirrors collection-tail display state onto the base hub thread key + listDeals row.
   *
   * @param {object} deal
   * @param {object} nextTail
   * @param {{ pending?: boolean, previousTail?: object|null, detailFetchedAt?: number|null }} [options]
   */
  _mirrorHubFromTail(deal, nextTail, { pending = false, previousTail = null, detailFetchedAt = null } = {}) {
    if (!deal || !nextTail || typeof nextTail !== "object") return;

    const baseThreadKey = this.threadKeyForDeal(deal, { collectionScoped: false });
    if (!baseThreadKey) return;

    const existingListRow = this._host.listDeals.find((d) => dealMatchesThreadKey(d, baseThreadKey));
    const existingBaseEntry = this._entry(baseThreadKey);
    const hubBase = existingListRow ?? existingBaseEntry?.tail ?? deal;
    const hubRow = buildHubDisplayRow(hubBase, nextTail);
    const now = Date.now();

    this.threadTails.set(baseThreadKey, {
      tail: hubRow,
      pending,
      previousTail: pending ? (previousTail ?? hubBase) : null,
      fetchedAt: now,
      detailFetchedAt: pending
        ? (existingBaseEntry?.detailFetchedAt ?? null)
        : (detailFetchedAt ?? existingBaseEntry?.detailFetchedAt ?? null),
    });
    this._replaceListRow(baseThreadKey, hubRow);
  }

  _dedupeListByPropertyId(deals = []) {
    if (!Array.isArray(deals) || deals.length <= 1) return deals;

    const sorted = this._sortNewestFirst(deals);
    /** @type {Map<string, object>} */
    const byProperty = new Map();

    for (const deal of sorted) {
      const propertyId = String(deal?.property_id ?? "").trim();
      if (!propertyId) {
        byProperty.set(`id:${rowDealId(deal)}`, deal);
        continue;
      }
      const existing = byProperty.get(propertyId);
      if (!existing) {
        byProperty.set(propertyId, deal);
        continue;
      }
      byProperty.set(propertyId, carryForwardDealEnrichment(existing, deal));
    }

    return this._sortNewestFirst(Array.from(byProperty.values()));
  }

  mergeListByThreadKey(incomingDeals = []) {
    if (!Array.isArray(incomingDeals)) return [];

    const byKey = new Map();
    for (const deal of incomingDeals) {
      const threadKey = this.threadKeyForDeal(deal);
      if (!threadKey) { byKey.set(`id:${rowDealId(deal)}`, deal); continue; }
      const entry = this._entry(threadKey);
      if (entry?.pending && entry.tail) {
        if (this._serverTailAdvances(entry, deal)) {
          this.registerTail(deal, { force: true, clearPending: true });
          byKey.set(threadKey, carryForwardDealEnrichment(deal, entry.tail));
        } else {
          byKey.set(threadKey, entry.tail);
        }
        continue;
      }
      const inMs = this._dealDateMs(deal);
      const exMs = this._dealDateMs(entry?.tail);
      if (entry?.tail && inMs && exMs && inMs < exMs) { byKey.set(threadKey, entry.tail); continue; }
      const previous = byKey.get(threadKey) ?? entry?.tail ?? null;
      byKey.set(threadKey, carryForwardDealEnrichment(deal, previous));
    }

    for (const [threadKey, entry] of this.threadTails.entries()) {
      if (!entry?.pending || !entry.tail) continue;
      if (!byKey.has(threadKey)) byKey.set(threadKey, entry.tail);
    }

    let merged = this._sortNewestFirst(Array.from(byKey.values()));
    if (Boolean(this._host.listFilters?.latestPerThread)) {
      merged = this._dedupeListByPropertyId(merged);
    }
    return merged;
  }

  // ── advance / revert ─────────────────────────────────────────────────

  /**
   * @param {object} deal
   * @param {object} patch
   * @param {{ kind?: 'append'|'inPlace'|'draftInsert', reflectDraftInList?: boolean, savedRow?: object, collectionScoped?: boolean, updateList?: boolean }} [options]
   * @returns {{ success: boolean, tail?: object, threadKey?: string }}
   */
  advanceThreadTail(
    deal, patch,
    { kind = "append", reflectDraftInList = true, savedRow = null, collectionScoped = false, collectionId = null, updateList } = {}
  ) {
    if (!deal || typeof deal !== "object") return { success: false };
    const threadKey = this.threadKeyForDeal(deal, { collectionScoped, collectionId });
    if (!threadKey) return { success: false };

    const entry = this._entry(threadKey);
    const listRow = this._host.listDeals.find((d) => dealMatchesThreadKey(d, threadKey));
    let currentTail = entry?.tail ?? deal;
    currentTail = carryForwardDealEnrichment(currentTail, listRow);
    currentTail = carryForwardDealEnrichment(currentTail, deal);
    const previousTail = entry?.pending && entry.previousTail ? entry.previousTail : currentTail;

    let nextTail;
    if (kind === "inPlace") {
      nextTail = mergeDealPatch(currentTail, patch);
    } else if (kind === "draftInsert") {
      const saved = savedRow && typeof savedRow === "object" ? savedRow : {};
      nextTail = substituteDealRow(saved, {
        priorRow: currentTail,
        listCustomer: this._host.listCustomer,
      });
      if (!reflectDraftInList) {
        nextTail = { ...nextTail, status: currentTail.status };
      }
    } else {
      const optimisticId = createOptimisticId();
      const merged = mergeDealPatch(currentTail, patch);
      const nowIso = new Date().toISOString();
      nextTail = {
        ...merged,
        id: optimisticId,
        deallens_id: optimisticId,
        insert_date: nowIso,
        last_modified: nowIso,
        metadata: { ...(merged.metadata || {}), previous_deallens_id: rowDealId(previousTail) },
      };
    }

    const now = Date.now();
    const keepsPending = kind === "inPlace" && Boolean(entry?.pending);
    const defaultUpdate = !collectionScoped && (kind !== "draftInsert" || reflectDraftInList);
    const shouldUpdate = typeof updateList === "boolean" ? Boolean(updateList) && !collectionScoped : defaultUpdate;
    const mirrorHub = collectionScoped && updateList !== false;
    const previousTailId = normalizedRowDealId(entry?.tail);
    const nextTailId = normalizedRowDealId(nextTail);
    const sameTailId = Boolean(previousTailId && nextTailId && previousTailId === nextTailId);

    runInAction(() => {
      this.threadTails.set(threadKey, {
        tail: nextTail,
        pending: kind === "append" || keepsPending,
        previousTail: kind === "append" ? previousTail : keepsPending ? entry.previousTail : null,
        fetchedAt: now,
        detailFetchedAt:
          kind === "draftInsert" || !sameTailId ? null : (entry?.detailFetchedAt ?? null),
      });
      if (shouldUpdate) {
        this._replaceListRow(threadKey, nextTail);
      } else if (mirrorHub) {
        const baseThreadKey = this.threadKeyForDeal(deal, { collectionScoped: false });
        const existingListRow = this._host.listDeals.find((d) => dealMatchesThreadKey(d, baseThreadKey));
        const existingBaseEntry = this._entry(baseThreadKey);
        const hubBase = existingListRow ?? existingBaseEntry?.tail ?? deal;
        this._mirrorHubFromTail(deal, nextTail, {
          pending: kind === "append" || keepsPending,
          previousTail: kind === "append" ? hubBase : null,
        });
      }
    });

    return { success: true, tail: nextTail, threadKey };
  }

  revertPendingThreadTail(threadKey, { updateList = true } = {}) {
    const entry = this._entry(threadKey);
    if (!entry?.pending || !entry.previousTail) return null;
    const previousTail = entry.previousTail;
    runInAction(() => {
      this.threadTails.set(threadKey, {
        tail: previousTail,
        pending: false,
        previousTail: null,
        fetchedAt: Date.now(),
        detailFetchedAt: entry.detailFetchedAt,
      });
      if (updateList) this._replaceListRow(threadKey, previousTail);
    });
    return previousTail;
  }

  revertPendingForDeal(deal, { collectionScoped = false, collectionId = null, updateList = true } = {}) {
    const threadKey = this.threadKeyForDeal(deal, { collectionScoped, collectionId });
    if (!threadKey) return null;
    const entry = this._entry(threadKey);
    if (!entry?.pending || !entry.previousTail) return null;

    const previousTail = entry.previousTail;
    runInAction(() => {
      this.revertPendingThreadTail(threadKey, { updateList: updateList && !collectionScoped });
      if (collectionScoped) {
        this._mirrorHubFromTail(deal, previousTail, { pending: false });
      }
    });
    return previousTail;
  }

  /**
   * Keeps the current tail but clears pending (mutation succeeded; optimistic tip is authoritative).
   *
   * @param {string} threadKey
   * @param {{ updateList?: boolean }} [options]
   * @returns {object | null}
   */
  confirmPendingThreadTail(threadKey, { updateList = true } = {}) {
    const entry = this._entry(threadKey);
    if (!entry?.tail) return null;
    runInAction(() => {
      this.threadTails.set(threadKey, {
        tail: entry.tail,
        pending: false,
        previousTail: null,
        fetchedAt: Date.now(),
        detailFetchedAt: entry.detailFetchedAt,
      });
      if (updateList) this._replaceListRow(threadKey, entry.tail);
    });
    return entry.tail;
  }

  /**
   * @param {object} deal
   * @param {{ collectionScoped?: boolean, collectionId?: string|null, updateList?: boolean }} [options]
   * @returns {object | null}
   */
  confirmPendingForDeal(deal, { collectionScoped = false, collectionId = null, updateList = true } = {}) {
    const threadKey = this.threadKeyForDeal(deal, { collectionScoped, collectionId });
    if (!threadKey) return null;

    const entry = this._entry(threadKey);
    if (!entry?.tail) return null;

    runInAction(() => {
      this.confirmPendingThreadTail(threadKey, { updateList: updateList && !collectionScoped });
      if (collectionScoped) {
        this._mirrorHubFromTail(deal, entry.tail, { pending: false });
      }
    });
    return entry.tail;
  }

  // ── reconcile ────────────────────────────────────────────────────────

  async reconcileThreadTail(
    deal,
    { userEmail, customer, collectionScoped = false, collectionId = null, revertOnFailure = true } = {},
  ) {
    const threadKey = this.threadKeyForDeal(deal, { collectionScoped, collectionId });
    const entry = this._entry(threadKey);
    if (!threadKey || !entry?.tail) return { success: false };

    const tailId = rowDealId(entry.tail);
    const needsReconcile =
      Boolean(entry.pending) || (tailId.startsWith("optimistic:") && Boolean(entry.previousTail ?? deal));
    if (!needsReconcile) return { success: false };

    const existing = this._inFlightReconcile.get(threadKey);
    if (existing) {
      if (existing.pendingId === rowDealId(entry.tail)) return existing.promise;
      return existing.promise.then(() =>
        this.reconcileThreadTail(deal, { userEmail, customer, collectionScoped, collectionId, revertOnFailure })
      );
    }

    const deallensId = rowDealId(entry.previousTail ?? deal);
    const email = String(userEmail ?? this._host.listUserEmail ?? "").trim();
    const cust = String(customer ?? deal?.customer ?? this._host.listCustomer ?? "").trim();
    if (!deallensId || !email || !cust) return { success: false };

    const genAtStart = this._host._resetGeneration;
    const pendingId = rowDealId(entry.tail);
    const promise = (async () => {
      for (const delayMs of RECONCILE_DELAYS_MS) {
        if (delayMs) await wait(delayMs);
        if (this._host._resetGeneration !== genAtStart) return { success: false, stale: true };

        const cur = this._entry(threadKey);
        if (!cur?.tail) return { success: false, stale: true };
        if (!cur.pending && !rowDealId(cur.tail).startsWith("optimistic:")) {
          const curId = normalizedRowDealId(cur.tail);
          const prevId = normalizedRowDealId(entry.previousTail);
          if (curId && curId !== prevId) return { success: true, deal: cur.tail };
          return { success: false, stale: true };
        }
        if (cur.pending && rowDealId(cur.tail) !== pendingId) return { success: false, stale: true };

        const result = await fetchDealLensThreadTip({
          deallens_id: deallensId, user_email: email, customer: cust,
        });
        if (!result.success || !this._serverTailAdvances(cur, result.deal)) continue;

        let reconciledDeal = result.deal;
        runInAction(() => {
          const registeredKey = this._registerFetchedDetail(
            threadKey, { sender: deal?.sender, user_id: deal?.receiver },
            result.deal, { collectionScoped },
          );
          reconciledDeal = this._entry(registeredKey)?.tail ?? result.deal;
          if (collectionScoped) {
            this._mirrorHubFromTail(deal, reconciledDeal, { pending: false });
          } else {
            this._replaceListRow(registeredKey, reconciledDeal);
          }
        });
        return { success: true, deal: reconciledDeal };
      }

      if (this._host._resetGeneration === genAtStart && revertOnFailure) {
        this.revertPendingForDeal(deal, { collectionScoped, collectionId, updateList: true });
        if (!collectionScoped) this._host._invalidateActiveOpportunitiesListCache();
      }
      return { success: false, reverted: revertOnFailure };
    })().finally(() => {
      if (this._inFlightReconcile.get(threadKey)?.promise === promise) {
        this._inFlightReconcile.delete(threadKey);
      }
    });

    this._inFlightReconcile.set(threadKey, { pendingId, promise });
    return promise;
  }

  // ── detail fetch / prefetch / resolve ────────────────────────────────

  _buildFetchParams({ rawUrlParam, routeSenderId, routeReceiverId, deal, customer: explicitCustomer }) {
    const customer = String(deal?.customer ?? this._host.listCustomer ?? explicitCustomer ?? "").trim();
    if (deal && typeof deal === "object") {
      const deallensId = normalizeDeallensIdFromUrl(String(deal.id ?? deal.deallens_id ?? ""));
      const sender = String(deal.sender ?? "").trim();
      const receiver = String(deal.receiver ?? "").trim();
      if (!deallensId || !sender || !receiver || !customer) return null;
      return { deallens_id: deallensId, sender, user_id: receiver, customer };
    }
    const deallensId = normalizeDeallensIdFromUrl(rawUrlParam);
    const sender = normalizeDealLensRouteSegment(routeSenderId);
    const receiver = normalizeDealLensRouteSegment(routeReceiverId);
    if (!deallensId || !sender || !receiver || !customer) return null;
    return { deallens_id: deallensId, sender, user_id: receiver, customer };
  }

  _registerFetchedDetail(threadKey, fetchParams, fetchedDeal, { collectionScoped = false } = {}) {
    if (!threadKey || !fetchedDeal || typeof fetchedDeal !== "object") return threadKey;

    const entry = this._entry(threadKey);
    if (entry?.pending && !this._serverTailAdvances(entry, fetchedDeal)) return threadKey;

    const existingTail = entry?.tail ?? null;
    const toRegister = substituteDealRow(fetchedDeal, {
      priorRow: existingTail,
      fetchParams,
      listCustomer: this._host.listCustomer,
    });

    const canonicalKey = this.threadKeyForDeal(toRegister, { collectionScoped });
    const targetKey = canonicalKey || threadKey;
    if (targetKey !== threadKey) {
      if (threadKey.startsWith("route:")) this._aliases.set(threadKey, targetKey);
      this.threadTails.delete(threadKey);
    }
    this.registerTail(toRegister, { detail: true, force: true, clearPending: true, threadKey: targetKey });
    return targetKey;
  }

  _fetchDetailForKey(threadKey, fetchParams, { genAtStart }) {
    let promise = this._inFlightDetail.get(threadKey);
    if (!promise) {
      promise = fetchDealLensByThread(fetchParams);
      this._inFlightDetail.set(threadKey, promise);
      promise
        .then((result) => {
          if (this._host._resetGeneration !== genAtStart) return;
          if (result.success && result.deal) {
            runInAction(() => this._registerFetchedDetail(threadKey, fetchParams, result.deal));
          } else if (IS_DEV && !result.success) {
            devLog("[TailCache] detail prefetch miss/error", { threadKey, result });
          }
        })
        .catch((err) => {
          if (IS_DEV) devLog("[TailCache] detail prefetch error", { threadKey, err });
        })
        .finally(() => this._inFlightDetail.delete(threadKey));
    }
    return promise;
  }

  prefetchDetail(deal) {
    if (!deal || typeof deal !== "object") return;
    const threadKey = this.threadKeyForDeal(deal);
    if (!threadKey) return;

    const entry = this._entry(threadKey);
    if (entry?.detailFetchedAt && entry.tail) return;
    if (this._inFlightDetail.has(threadKey)) return;

    this.registerTail(deal);
    const fetchParams = this._buildFetchParams({ deal });
    if (!fetchParams) return;
    void this._fetchDetailForKey(threadKey, fetchParams, { genAtStart: this._host._resetGeneration });
  }

  peekDetail(rawUrlParam, routeSenderId, routeReceiverId, deal = null) {
    const threadKey = this._resolveKey(deal, rawUrlParam, routeSenderId, routeReceiverId);
    if (!threadKey) return null;
    return this._entry(threadKey)?.tail ?? null;
  }

  async resolveDetailForThread({
    rawUrlParam, routeSenderId, routeReceiverId,
    deal, forceRefresh = false, viewerEmail, customer,
  } = {}) {
    const threadKey = this._resolveKey(deal, rawUrlParam, routeSenderId, routeReceiverId);
    if (!threadKey) return { success: false, message: "Missing deallens_id, sender, or user_id." };

    if (forceRefresh) {
      this.threadTails.delete(threadKey);
      this._inFlightDetail.delete(threadKey);
    }

    const hadInFlight = !forceRefresh && this._inFlightDetail.has(threadKey);
    const entry = this._entry(threadKey);
    const cachedDeal = entry?.tail;

    if (cachedDeal && entry?.detailFetchedAt && !forceRefresh) {
      if (
        this._shouldBlockThreadForViewer({ deal: null, routeSenderId, routeReceiverId, viewerEmail }) ||
        this._shouldBlockCachedTailForViewer(cachedDeal, viewerEmail)
      ) {
        return this._viewerMismatchResult();
      }
      if (!this._detailCacheStaleForBootstrap(cachedDeal, deal)) {
        return { success: true, deal: mergeDealLensSwipeStatus(cachedDeal, deal), source: "cache" };
      }
    }

    if (
      !hadInFlight &&
      this._shouldBlockThreadForViewer({ deal, rawUrlParam, routeSenderId, routeReceiverId, viewerEmail })
    ) {
      return this._viewerMismatchResult();
    }

    const fetchParams = this._buildFetchParams({ rawUrlParam, routeSenderId, routeReceiverId, deal, customer });
    if (!fetchParams) return { success: false, message: "Missing deallens_id, sender, or user_id." };

    const genAtStart = this._host._resetGeneration;
    const promise = this._fetchDetailForKey(threadKey, fetchParams, { genAtStart });
    const result = await promise;

    if (this._host._resetGeneration !== genAtStart) {
      return { success: false, message: "Deal load cancelled.", stale: true };
    }

    if (result.success && result.deal) {
      let registeredKey = threadKey;
      runInAction(() => {
        registeredKey = this._registerFetchedDetail(threadKey, fetchParams, result.deal);
      });
      const registeredDeal = this._entry(registeredKey)?.tail ?? result.deal;
      return {
        ...result,
        deal: mergeDealLensSwipeStatus(registeredDeal, deal),
        source: hadInFlight ? "in-flight" : "fetch",
      };
    }

    const pendingTail = this._entry(threadKey)?.tail;
    if (pendingTail) {
      return {
        success: true,
        provisional: true,
        deal: mergeDealLensSwipeStatus(pendingTail, deal),
        source: "pending-tail",
      };
    }

    return result;
  }
}

export default DealLensThreadTailCache;
